import type { RenderStateCamera } from "@novorender/api";
import { useRef } from "react";

import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded } from "components/toolbars/mobile/slice";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useLazyHidden } from "contexts/hidden";
import {
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useLazyHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import {
    GroupStatus,
    InternalTemporaryGroup,
    objectGroupsActions,
    useDispatchObjectGroups,
    useLazyObjectGroups,
} from "contexts/objectGroups";
import { selectionBasketActions, useDispatchSelectionBasket, useLazySelectionBasket } from "contexts/selectionBasket";
import { shownActions, useDispatchShown, useLazyShown } from "contexts/shown";
import { coordinatesActions, selectCoordinates, selectCoordinatesEditSessionStartSnapshot } from "features/coordinates";
import {
    measureActions,
    selectMeasureEntities,
    selectObjectMeasure,
    selectObjectMeasureEditSessionStartSnapshot,
} from "features/measure/measureSlice";
import {
    clippingOutlineLaserActions,
    selectLaserEditSessionStartSnapshot,
    selectOutlineLasers,
} from "features/outlineLaser/outlineLaserSlice";
import {
    pointLineActions,
    selectPointLineEditSessionStartSnapshot,
    selectPointLines,
} from "features/pointLine/pointLineSlice";
import { capturePropertyFilter, propertiesActions } from "features/properties/slice";
import { useSetupVersionComparisonPropertyTree } from "features/propertyTree/hooks/useSetupVersionComparisonPropertyTree";
import {
    Picker,
    renderActions,
    selectClippingEditSessionStartSnapshot,
    selectClippingPlanes,
    selectHomeViewpoint,
    selectInitialCamera,
    selectMainObject,
    selectMainObjectPick,
    selectMainObjects,
    selectPicker,
} from "features/render";
import { cloneCameraState } from "features/render/hooks/useHandleCameraMoved";
import { CameraType } from "features/render/types";
import { loadScene } from "features/render/utils";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";
import { useSetViewpoint } from "features/viewpoints/useSetViewpoint";
import { collectPdfHiddenIds, useObjectTreeHidden } from "hooks/useObjectTreeHidden";
import { useSceneId } from "hooks/useSceneId";
import {
    explorerActions,
    selectBackendProvider,
    selectHideSpacesRooms,
    selectIsVersionComparison,
    selectPipelineConfig,
} from "slices/explorer";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";
import { telemetry } from "telemetry";
import { captureVisibilitySnapshot, restoreVisibility } from "utils/objectActions";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

export type ResetViewFn = (options?: {
    resetCamera?: boolean;
    resetVersionComparisonPropertyTree?: boolean;
    preservePicker?: boolean;
}) => Promise<void>;

export function useResetView(): ResetViewFn {
    const {
        state: { view, db, internalEventEmitter },
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const store = useAppStore();
    const setViewpoint = useSetViewpoint();
    const setViewpointRef = useRef(setViewpoint);
    setViewpointRef.current = setViewpoint;
    const dispatch = useAppDispatch();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const dispatchShown = useDispatchShown();
    const objectGroups = useLazyObjectGroups();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const setupVersionComparisonPropertyTree = useSetupVersionComparisonPropertyTree();
    const lazyHighlighted = useLazyHighlighted();
    const lazyHidden = useLazyHidden();
    const lazySelectionBasket = useLazySelectionBasket();
    const lazyHighlightCollections = useLazyHighlightCollections();
    const lazyShown = useLazyShown();
    const { pdfObjectIds } = useObjectTreeHidden();
    const pdfObjectIdsRef = useRef(pdfObjectIds);
    pdfObjectIdsRef.current = pdfObjectIds;

    /**
     * Procore 2D sheets are hidden deliberately and are not object-tree items, so a
     * reset leaves them hidden. Until discovery resolves they cannot be told apart
     * from object-tree ids, and clearing everything is the only option that still
     * resets the view.
     */
    const clearHiddenObjects = () => {
        const sheetIds = pdfObjectIdsRef.current;
        const { ids, toggleSnapshot } = lazyHidden.current;
        dispatchHidden(hiddenActions.setIds(sheetIds ? collectPdfHiddenIds(ids, sheetIds) : []));
        dispatchHidden(hiddenActions.setToggleSnapshot(sheetIds ? collectPdfHiddenIds(toggleSnapshot, sheetIds) : []));
    };

    const clearObjectHighlights = () => {
        clearHiddenObjects();
        dispatchHighlighted(highlightActions.setIds([]));
        dispatchSelectionBasket(selectionBasketActions.set([]));
        dispatchHighlightCollections(highlightCollectionsActions.clearAll());
        dispatchShown(shownActions.setIds([]));
    };

    const lastSavedCameraState = useRef<RenderStateCamera | null>(null);

    return async ({
        resetCamera = true,
        resetVersionComparisonPropertyTree = true,
        preservePicker = false,
    }:
        | {
              resetCamera?: boolean;
              resetVersionComparisonPropertyTree?: boolean;
              preservePicker?: boolean;
          }
        | undefined = {}) => {
        const state = store.getState();
        const defaultCamera = selectInitialCamera(state);
        const homeViewpoint = selectHomeViewpoint(state);
        const backendProvider = selectBackendProvider(state);
        const hideSpacesRooms = selectHideSpacesRooms(state);
        const isVersionComparison = selectIsVersionComparison(state);
        // Only fetch pipelineConfig for Novorender backend (used in version comparison)
        const pipelineConfig = backendProvider !== "procore" ? selectPipelineConfig(state) : undefined;

        // Capture the pre-reset redux + context state so the undo callback can
        // restore it (resetView below clears these slices).
        const visibilityBefore = captureVisibilitySnapshot(state);
        const coordinates = selectCoordinates(state);
        const lasers = selectOutlineLasers(state);
        const pointLines = selectPointLines(state);
        const dimensions = selectMeasureEntities(state);
        // Both reset paths wipe this - the generic `renderActions.resetView` and the Procore
        // home-viewpoint apply, since a viewpoint never carries an object-to-object measurement.
        const objectMeasure = selectObjectMeasure(state);
        const sectionPlanes = selectClippingPlanes(state);
        const currentPicker = selectPicker(state);
        const activeToolMode = selectActiveToolMode(state);
        const bottomToolbarActiveTab = selectActiveTab(state);
        const bottomToolbarIsExpanded = selectIsExpanded(state);
        const laserSnapshot = selectLaserEditSessionStartSnapshot(state);
        const coordinatesSnapshot = selectCoordinatesEditSessionStartSnapshot(state);
        const pointLineSnapshot = selectPointLineEditSessionStartSnapshot(state);
        const clippingSnapshot = selectClippingEditSessionStartSnapshot(state);
        const objectMeasureSnapshot = selectObjectMeasureEditSessionStartSnapshot(state);
        const hiddenIds = snapshotUndoIds(lazyHidden.current.idArr);
        const hiddenToggleSnapshot = snapshotUndoIds(lazyHidden.current.toggleSnapshot);
        const highlightedIds = snapshotUndoIds(lazyHighlighted.current.idArr);
        const selectionBasketIds = snapshotUndoIds(lazySelectionBasket.current.idArr);
        const highlightCollectionsSnapshot = lazyHighlightCollections.current;
        const propertyFilter = capturePropertyFilter(state);
        const mainObject = selectMainObject(state);
        const mainObjects = selectMainObjects(state);
        const mainObjectPick = selectMainObjectPick(state);
        const shown = lazyShown.current.ids;

        function undoResetView() {
            const dimensionsEntitiesToRestore = dimensions.map((set) => structuredClone(set));
            const objectMeasureToRestore = structuredClone(objectMeasure);

            dispatchHidden(hiddenActions.setIds(restoreUndoIds(hiddenIds)));
            dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(hiddenToggleSnapshot)));
            dispatchHighlighted(highlightActions.setIds(restoreUndoIds(highlightedIds)));
            // The semantic selection goes back with the highlight set it came from, so the
            // object tree, Properties and the bulk actions menu all describe the same pick.
            // The surface pick is only meaningful against its own `mainObject`, hence together.
            dispatch(renderActions.setMainObject(mainObject));
            dispatch(renderActions.setMainObjects(mainObjects));
            dispatch(renderActions.setMainObjectPick(mainObjectPick));
            dispatchSelectionBasket(selectionBasketActions.set(restoreUndoIds(selectionBasketIds)));
            dispatchHighlightCollections(highlightCollectionsActions.setAll(highlightCollectionsSnapshot));
            dispatchShown(shownActions.setIds(shown));
            // Restores default visibility, X-ray's pick opacity threshold, preIsolationVisibility
            // and plain-isolation (active or paused) together — resetView clears all of them.
            restoreVisibility({ dispatch, snapshot: visibilityBefore });
            // The Properties checkboxes are the UI for the highlight + X-ray filter restored
            // above, so they come back with it rather than staying cleared.
            dispatch(propertiesActions.setPropertyFilter(propertyFilter));
            dispatch(coordinatesActions.setCoordinates(coordinates));
            dispatch(clippingOutlineLaserActions.setLasers(lasers));
            dispatch(pointLineActions.setPointLines(pointLines));
            if (dimensionsEntitiesToRestore.length === 1) {
                dispatch(measureActions.setSelectedEntities(dimensionsEntitiesToRestore[0]));
            } else {
                dimensionsEntitiesToRestore.forEach((entities, i) =>
                    dispatch(measureActions.setMeasureSet({ idx: i, entities })),
                );
            }
            dispatch(measureActions.setObjectMeasure(objectMeasureToRestore));
            const clonedSectionPlanes = cloneClippingPlanes(sectionPlanes.planes || []);
            dispatch(
                renderActions.setClippingPlanes(
                    clonedSectionPlanes.length === 0
                        ? { planes: clonedSectionPlanes, enabled: false }
                        : { planes: clonedSectionPlanes, enabled: true },
                ),
            );

            if (lastSavedCameraState.current) {
                dispatch(
                    renderActions.setCamera({
                        type:
                            lastSavedCameraState.current.kind === "pinhole"
                                ? CameraType.Pinhole
                                : CameraType.Orthographic,
                        goTo: cloneCameraState(lastSavedCameraState.current),
                    }),
                );
            }

            // Restore edit-session snapshots so a subsequent confirm/cancel in the
            // restored tool mode can revert the in-session changes correctly.
            dispatch(clippingOutlineLaserActions.setEditSessionStartSnapshot(laserSnapshot));
            dispatch(coordinatesActions.setEditSessionStartSnapshot(coordinatesSnapshot));
            dispatch(pointLineActions.setEditSessionStartSnapshot(pointLineSnapshot));
            dispatch(renderActions.setClippingEditSessionStartSnapshot(clippingSnapshot));
            dispatch(measureActions.setObjectMeasureEditSessionStartSnapshot(objectMeasureSnapshot));

            // Restore the Procore tool-mode UI: active tool mode + bottom-toolbar tab/expansion.
            dispatch(renderActions.setPicker(currentPicker));
            dispatch(procoreUxActions.setActiveToolMode(activeToolMode));
            dispatch(bottomToolbarActions.setActiveTab(bottomToolbarActiveTab));
            if (bottomToolbarIsExpanded) {
                dispatch(bottomToolbarActions.expand());
            } else {
                dispatch(bottomToolbarActions.collapse());
            }
        }

        async function resetView() {
            try {
                // make reusable in case setting the home viewpoint fails and we need to fallback to a generic reset
                const procoreResetView = () => {
                    const initialCamera = resetCamera ? structuredClone(defaultCamera) : undefined;

                    const sceneData = {
                        title: "",
                        objectGroups: [],
                        settings: undefined,
                        customProperties: {},
                        projectId: "",
                    };

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );

                    // Spaces freeze ids are fetched once on init into renderSlice and survive
                    // resetView — no refetch on home/reset (the set is stable for this scene version).
                    if (!hideSpacesRooms) {
                        dispatch(renderActions.clearSpacesFrozenObjectIds());
                    }
                };

                let restoredViewpoint = false;

                // For Procore backend, don't call loadScene (Novorender API)
                // Scene data is already loaded via Procore APIs
                if (backendProvider === "procore") {
                    if (resetCamera && homeViewpoint) {
                        try {
                            dispatch(renderActions.clearAllObjectColor());
                            await setViewpointRef.current(homeViewpoint, "system", { ignorePdfs: true });
                            restoredViewpoint = true;
                        } catch (e) {
                            console.warn("Failed to apply home viewpoint:", e);
                            procoreResetView();
                        }
                    } else {
                        procoreResetView();
                    }
                } else {
                    // Novorender backend - use loadScene API
                    const [{ url: _url, db: _db, ...sceneData }, camera] = await loadScene(sceneId);
                    const groups = sceneData.objectGroups ?? [];
                    const initialCamera = resetCamera ? (camera ?? structuredClone(defaultCamera)) : undefined;

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );

                    dispatchObjectGroups(
                        objectGroupsActions.set(
                            objectGroups.current
                                .filter((group) => group.grouping !== InternalTemporaryGroup.BIMcollab)
                                .map((group) => {
                                    const saved = groups.find((grp) => group.id === grp.id);

                                    if (saved) {
                                        let status: GroupStatus;
                                        if (saved.selected) {
                                            status = GroupStatus.Selected;
                                        } else if (saved.hidden) {
                                            status = GroupStatus.Hidden;
                                        } else if (saved.frozen) {
                                            status = GroupStatus.Frozen;
                                        } else {
                                            status = GroupStatus.None;
                                        }
                                        return { ...group, status, opacity: saved.opacity ?? 0 };
                                    }

                                    return group;
                                })
                                .sort(
                                    (a, b) =>
                                        groups.findIndex((grp) => grp.id === a.id) -
                                        groups.findIndex((grp) => grp.id === b.id),
                                ),
                        ),
                    );
                }

                // Emit viewReset event through internal EventEmitter.
                // NOTE: This intentionally happens immediately after render.resetView has
                // completed, but before clearing clipping outlines, coordinates and object
                // highlights below. Consumers of the event rely on seeing the freshly
                // reset scene/camera state while any existing auxiliary UI state is still
                // present, so the ordering here is part of the event's contract.
                // On the Procore home-viewpoint branch the reset *is* the viewpoint apply,
                // so the event follows that rather than a generic reset the camera would
                // then move away from — a consumer reading the camera here sees the one the
                // reset settled on either way. What it no longer sees is the auxiliary state
                // the viewpoint replaced, since applying it is what restored coordinates,
                // lasers and point lines in the first place.
                // In lib mode, WebviewerBridge will propagate this to the public webviewer.events API.
                if (internalEventEmitter) {
                    await internalEventEmitter.emit("viewReset", {
                        timestamp: Date.now(),
                        sceneId,
                        cameraReset: resetCamera,
                    });
                }

                // Viewpoint manages measurements and highlights/hidden itself
                if (!restoredViewpoint) {
                    dispatch(clippingOutlineLaserActions.clear());
                    dispatch(clippingOutlineLaserActions.clearEditSessionStartSnapshot());
                    dispatch(coordinatesActions.setHideCoordinateGizmo(false));
                    dispatch(coordinatesActions.clear());
                    dispatch(coordinatesActions.clearEditSessionStartSnapshot());
                    dispatch(pointLineActions.clearEditSessionStartSnapshot());
                    dispatch(renderActions.clearClippingEditSessionStartSnapshot());

                    clearObjectHighlights();
                }

                if (!preservePicker) {
                    dispatch(renderActions.setPicker(Picker.Object));
                    dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Default));
                }

                // Only restore object groups for Novorender backend
                // Procore doesn't have saved groups yet

                if (resetVersionComparisonPropertyTree && db && view?.renderState.scene?.url && isVersionComparison) {
                    void setupVersionComparisonPropertyTree(db, view, pipelineConfig?.version, backendProvider);
                }
            } catch (e) {
                console.error("Failed to reset view:", e);
                telemetry.trackCaughtException({
                    source: "home.useResetView.resetView",
                    category: "data",
                    error: e,
                    severity: "error",
                });
                dispatch(
                    explorerActions.setSnackbarMessage({
                        msg: "Failed to reset view. Please try again.",
                        closeAfter: 4000,
                    }),
                );
            }
        }
        dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
        if (view?.renderState.camera) {
            lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
        }
        await resetView();

        dispatch(
            undoRedoActions.push({
                description: "Reset View",
                undoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    undoResetView();
                },
                redoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    await resetView();
                },
            }),
        );
    };
}
